import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, streamAsk } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { PaperPlaneTilt, Warning, ArrowSquareOut, Lightbulb, ChatTeardropText, CheckCircle, ShieldCheck } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

const SUGGESTIONS = [
  "What's the move-in inspection process?",
  "What does The GK Team at Signature do?",
  "Onboarding checklist for new hires?",
];

export default function Chat() {
  const { cid } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [convId, setConvId] = useState(cid || null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (cid) {
      setConvId(cid);
      api.get(`/conversations/${cid}/messages`).then(r => setMessages(r.data)).catch(() => {
        toast.error("Couldn't load conversation");
        nav("/");
      });
    } else {
      setConvId(null);
      setMessages([]);
    }
  }, [cid, nav]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || sending) return;
    setInput("");
    setSending(true);

    // Optimistic user message
    const optimistic = { id: `tmp-${Date.now()}`, role: "user", content: q, citations: [], answered: true };
    // Streaming placeholder for the assistant response
    const streamId = `stream-${Date.now()}`;
    const draft = {
      id: streamId, role: "assistant", content: "", citations: [], answered: true,
      __streaming: true, __verifying: false, __verified: false, __corrected: false,
    };
    setMessages(m => [...m, optimistic, draft]);

    const patch = (partial) =>
      setMessages(m => m.map(x => (x.id === streamId ? { ...x, ...partial } : x)));

    try {
      await streamAsk(
        { question: q, conversation_id: convId },
        {
          onMeta: (d) => {
            if (!convId && d?.conversation_id) {
              setConvId(d.conversation_id);
              window.history.replaceState(null, "", `/c/${d.conversation_id}`);
            }
          },
          onClarifying: (d) => {
            patch({ id: d?.message_id || streamId, content: d?.content || "", __streaming: false });
          },
          onToken: (d) => {
            const t = d?.text ?? "";
            if (!t) return;
            setMessages(m => m.map(x =>
              x.id === streamId ? { ...x, content: (x.content || "") + t } : x
            ));
          },
          onCitations: (d) => {
            patch({ citations: d?.citations || [] });
          },
          onVerifying: () => {
            patch({ __streaming: false, __verifying: true });
          },
          onVerified: () => {
            patch({ __verifying: false, __verified: true });
          },
          onCorrection: (d) => {
            patch({
              content: d?.revised_content || "",
              citations: d?.citations || [],
              __verifying: false, __verified: true, __corrected: true,
            });
          },
          onDone: (d) => {
            // Swap streamId placeholder with the persisted DB id.
            setMessages(m => m.map(x => {
              if (x.id !== streamId) return x;
              const answered = d?.answered !== false;
              return {
                ...x,
                id: d?.message_id || x.id,
                answered,
                content: answered ? x.content : "I don't have enough information from Tracey's training materials to answer that.",
                citations: answered ? x.citations : [],
                __streaming: false,
                __verifying: false,
              };
            }));
          },
          onError: (d) => {
            toast.error(d?.detail || "Something went wrong");
            setMessages(m => m.filter(x => x.id !== streamId));
          },
        },
      );
    } catch (e) {
      toast.error(e?.message || "Something went wrong");
      setMessages(m => m.filter(x => x.id !== streamId && x.id !== optimistic.id));
    } finally {
      setSending(false);
    }
  };

  const empty = messages.length === 0;

  return (
    <div className="flex flex-col h-screen md:h-screen">
      {/* Sticky header */}
      <header className="glass-header sticky top-0 z-20 px-4 md:px-8 py-3 flex items-center gap-3" data-testid="chat-header">
        <div className="w-9 h-9 rounded-full bg-gk-red text-white flex items-center justify-center md:hidden">
          <ChatTeardropText size={16} weight="fill" />
        </div>
        <div className="flex-1">
          <div className="font-heading text-xl md:text-2xl leading-none text-gk-charcoal">Ask Tracey</div>
          <div className="text-[11px] text-ink-soft mt-1">An internal knowledge assistant for The GK Team at Signature — grounded only in Tracey's approved training materials.</div>
        </div>
        {user?.role === "team" && (
          <span className="hidden md:inline-block text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-surface text-ink-soft border border-line">Read-only</span>
        )}
      </header>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 md:px-8 py-6" data-testid="chat-messages">
        <div className="max-w-3xl mx-auto space-y-8">
          {empty && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="py-8 md:py-16">
              <h1 className="font-heading text-4xl md:text-5xl leading-tight text-gk-charcoal">
                Hi {user?.name?.split(" ")[0] || "there"},<br/>
                <span className="text-ink-soft italic">what would you like to know?</span>
              </h1>
              <p className="mt-4 text-ink-soft max-w-lg">
                Ask Tracey is an internal knowledge assistant for The GK Team at Signature, grounded only in Tracey's approved training materials. Every answer includes citations and links.
              </p>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
                {SUGGESTIONS.map((s, i) => (
                  <button key={i} data-testid={`suggestion-${i}`}
                    onClick={() => send(s)}
                    className="text-left px-4 py-3 rounded-2xl border border-line bg-surface/50 hover:bg-surface hover:-translate-y-[1px] transition-transform">
                    <Lightbulb size={16} className="mb-2 text-gk-red" weight="duotone" />
                    <div className="text-sm">{s}</div>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          <AnimatePresence initial={false}>
            {messages.map((m) => (
              <motion.div key={m.id}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                {m.role === "assistant" ? (
                  <div className="flex gap-3 max-w-full">
                    <div className="w-9 h-9 shrink-0 rounded-full bg-gk-red text-white flex items-center justify-center mt-1">
                      <ChatTeardropText size={16} weight="fill" />
                    </div>
                    <div className="flex-1 min-w-0">
                      {!m.answered ? (
                        <div className="rounded-2xl border border-gk-red/20 bg-warn-bg text-warn-fg p-4" data-testid="not-enough-info">
                          <div className="flex items-center gap-2 mb-1">
                            <Warning size={18} weight="fill" />
                            <div className="font-medium">Not enough information</div>
                          </div>
                          <p className="text-sm">{m.content}</p>
                          <p className="text-xs mt-2 opacity-80">A manager has been notified so they can add the missing documentation to Google Drive.</p>
                        </div>
                      ) : (
                        <>
                          <div className="font-heading text-lg md:text-xl leading-snug whitespace-pre-wrap text-gk-charcoal" data-testid={`assistant-content-${m.id}`}>
                            {m.content || (m.__streaming ? "…" : "")}
                            {m.__streaming && m.content && (
                              <span className="inline-block w-[2px] h-4 bg-gk-red ml-1 align-baseline animate-pulse" aria-hidden />
                            )}
                          </div>

                          {(m.__verifying || m.__verified || m.__corrected) && (
                            <div className="mt-2 flex items-center gap-2 text-[11px]">
                              {m.__verifying && (
                                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-line bg-surface text-ink-soft" data-testid="verifying-pill">
                                  <span className="dot" /> Verifying against Tracey's training…
                                </span>
                              )}
                              {!m.__verifying && m.__corrected && (
                                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-line bg-warn-bg text-warn-fg" data-testid="corrected-pill">
                                  <ShieldCheck size={12} weight="fill" /> Corrected to match approved training
                                </span>
                              )}
                              {!m.__verifying && m.__verified && !m.__corrected && (
                                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-line bg-surface text-ink-soft" data-testid="verified-pill">
                                  <CheckCircle size={12} weight="fill" /> Verified
                                </span>
                              )}
                            </div>
                          )}

                          {m.citations?.length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-2" data-testid="citations">
                              {m.citations.map((c, i) => (
                                <a key={i} href={c.drive_url || "#"} target="_blank" rel="noreferrer"
                                  onClick={(e) => { if (!c.drive_url) e.preventDefault(); }}
                                  className="citation-chip">
                                  <ArrowSquareOut size={11} weight="bold" />
                                  {c.document_name}
                                </a>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="msg-user px-4 py-2.5 max-w-[85%] text-[15px] leading-snug whitespace-pre-wrap">
                    {m.content}
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>

          {sending && !messages.some(m => m.__streaming || m.__verifying) && (
            <div className="flex gap-3">
              <div className="w-9 h-9 shrink-0 rounded-full bg-gk-red text-white flex items-center justify-center">
                <ChatTeardropText size={16} weight="fill" />
              </div>
              <div className="flex items-center gap-1 mt-3"><span className="dot"/><span className="dot"/><span className="dot"/></div>
            </div>
          )}
        </div>
      </div>

      {/* Input */}
      <div className="sticky bottom-0 md:bottom-0 px-4 md:px-8 pb-4 md:pb-6 pt-2">
        <div className="max-w-3xl mx-auto">
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="glass-input rounded-full flex items-center gap-2 p-1.5 shadow-sm" data-testid="chat-form">
            <input
              value={input}
              onChange={(e)=>setInput(e.target.value)}
              placeholder="Ask Tracey anything…"
              className="flex-1 bg-transparent px-4 py-2.5 outline-none text-[15px]"
              data-testid="chat-input"
            />
            <Button type="submit" disabled={!input.trim() || sending} data-testid="send-button"
              className="rounded-full h-10 w-10 p-0 bg-gk-red hover:bg-gk-red-bright">
              <PaperPlaneTilt size={16} weight="fill" />
            </Button>
          </form>
          <div className="text-[11px] text-ink-soft text-center mt-2">
            Tracey only answers from approved training materials. Unanswered questions notify a manager.
          </div>
        </div>
      </div>
    </div>
  );
}
